/**
 * `/api/client-portal/*` for the signed-in client, and the firm's
 * `/api/foreign_nationals/:id/portal*` routes. The server resolves the client
 * from the session, so client routes carry no client id.
 */
import axiosInstance from "@/api/axiosConfig";
import { APIError, handleAPIError } from "@/api/utils/errors";

import { clientPortalPreview, PREVIEW_HEADER } from "./preview";
import type {
  FirmPortalCase,
  FirmPortalCaseUpdate,
  FirmSignatureRequest,
  InkUpload,
  PortalAccessState,
  PortalCaseDetail,
  PortalCorrection,
  PortalCorrectionDraft,
  PortalDocument,
  PortalFile,
  PortalHome,
  PortalMe,
  PortalProfile,
  PortalReceiptStatus,
  PortalSignature,
  PortalStageOption,
  SignatureDraft,
  SignatureRequestDraft,
  UploadCheck
} from "./types";

const call = async <T>(run: () => Promise<{ data: T }>): Promise<T> => {
  try {
    const { data } = await run();
    return data;
  } catch (error) {
    const apiError = handleAPIError(error);
    throw apiError;
  }
};

const BASE = "/client-portal";

axiosInstance.interceptors.request.use((config) => {
  const preview = clientPortalPreview();
  if (preview && config.url?.startsWith(BASE)) {
    config.headers.set(PREVIEW_HEADER, preview);
  }
  return config;
});

/** The `code` the server attaches to portal errors ("expired", "off"...). */
export const portalErrorCode = (error: unknown): string | null => {
  if (!(error instanceof APIError)) return null;
  const details = error.details as { code?: unknown } | undefined;
  return typeof details?.code === "string" ? details.code : null;
};

export const ClientPortalAPI = {
  me: () => call(() => axiosInstance.get<PortalMe>(`${BASE}/me`)),
  home: () => call(() => axiosInstance.get<PortalHome>(`${BASE}/home`)),
  documents: () =>
    call(() =>
      axiosInstance.get<{ documents: PortalDocument[] }>(`${BASE}/documents`)
    ).then((d) => d.documents),
  caseDetail: (caseId: string) =>
    call(() =>
      axiosInstance.get<PortalCaseDetail>(
        `${BASE}/cases/${encodeURIComponent(caseId)}`
      )
    ),
  receiptStatus: (caseId: string, receiptId: string) =>
    call(() =>
      axiosInstance.get<PortalReceiptStatus>(
        `${BASE}/cases/${encodeURIComponent(
          caseId
        )}/receipts/${encodeURIComponent(receiptId)}/status`
      )
    ),
  profile: () =>
    call(() => axiosInstance.get<PortalProfile>(`${BASE}/profile`)),
  suggestCorrection: (draft: PortalCorrectionDraft) =>
    call(() =>
      axiosInstance.post<PortalCorrection>(`${BASE}/corrections`, draft)
    ),
  withdrawCorrection: (correctionId: string) =>
    call(() =>
      axiosInstance.delete<{ withdrawn: boolean }>(
        `${BASE}/corrections/${encodeURIComponent(correctionId)}`
      )
    ),
  openTask: (taskId: string) =>
    call(() =>
      axiosInstance.get<{ path: string }>(
        `${BASE}/tasks/${encodeURIComponent(taskId)}/open`
      )
    ).then((d) => d.path),
  /** Reads back what the client just uploaded. Never blocks the upload. */
  checkDocument: (documentId: string) =>
    call(() =>
      axiosInstance.post<UploadCheck>(
        `${BASE}/documents/${encodeURIComponent(documentId)}/check`
      )
    ),
  signature: (requestId: string) =>
    call(() =>
      axiosInstance.get<PortalSignature>(
        `${BASE}/signatures/${encodeURIComponent(requestId)}`
      )
    ),
  /** E-sign: the document; ink: just the pages to print; signed: the copy. */
  signatureFile: (requestId: string) =>
    call(() =>
      axiosInstance.get<PortalFile>(
        `${BASE}/signatures/${encodeURIComponent(requestId)}/file`
      )
    ),
  sign: (requestId: string, draft: SignatureDraft) =>
    call(() =>
      axiosInstance.post<PortalSignature & { copy_emailed: boolean }>(
        `${BASE}/signatures/${encodeURIComponent(requestId)}/sign`,
        draft
      )
    ),
  uploadInk: (requestId: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return call(() =>
      axiosInstance.post<InkUpload>(
        `${BASE}/signatures/${encodeURIComponent(requestId)}/ink`,
        form
      )
    );
  },
  submitInk: (requestId: string) =>
    call(() =>
      axiosInstance.post<PortalSignature>(
        `${BASE}/signatures/${encodeURIComponent(requestId)}/submit`
      )
    ),
  /** Public. Answers the same either way, so it can't be used to find clients. */
  sendSignInLink: (email: string) =>
    call(() =>
      axiosInstance.post<{ sent: boolean }>(`${BASE}/sign-in-link`, { email })
    ),
  /** Public. Exchanges an emailed link for a Firebase custom token. */
  redeem: (token: string) =>
    call(() =>
      axiosInstance.post<{ custom_token: string; foreign_national_id: string }>(
        `${BASE}/redeem`,
        { token }
      )
    )
};

const firmBase = (clientId: string) =>
  `/foreign_nationals/${encodeURIComponent(clientId)}/portal`;

export const FirmPortalAPI = {
  state: (clientId: string) =>
    call(() => axiosInstance.get<PortalAccessState>(firmBase(clientId))),
  invite: (clientId: string) =>
    call(() =>
      axiosInstance.post<PortalAccessState>(`${firmBase(clientId)}/invite`)
    ),
  cases: (clientId: string) =>
    call(() =>
      axiosInstance.get<{
        cases: FirmPortalCase[];
        stages: PortalStageOption[];
      }>(`${firmBase(clientId)}/cases`)
    ),
  updateCase: (clientId: string, caseId: string, patch: FirmPortalCaseUpdate) =>
    call(() =>
      axiosInstance.put<FirmPortalCase>(
        `${firmBase(clientId)}/cases/${encodeURIComponent(caseId)}`,
        patch
      )
    )
};

const correctionsBase = (clientId: string) =>
  `/foreign_nationals/${encodeURIComponent(clientId)}/corrections`;

/** The firm reviewing corrections a client suggested from the portal. */
export const FirmCorrectionsAPI = {
  list: (clientId: string) =>
    call(() =>
      axiosInstance.get<{ corrections: PortalCorrection[] }>(
        correctionsBase(clientId)
      )
    ).then((d) => d.corrections),
  /** `fields`: accept only these of the proposed values. */
  accept: (clientId: string, correctionId: string, fields?: string[]) =>
    call(() =>
      axiosInstance.post<PortalCorrection>(
        `${correctionsBase(clientId)}/${encodeURIComponent(
          correctionId
        )}/accept`,
        fields ? { fields } : {}
      )
    ),
  decline: (clientId: string, correctionId: string, reason?: string) =>
    call(() =>
      axiosInstance.post<PortalCorrection>(
        `${correctionsBase(clientId)}/${encodeURIComponent(
          correctionId
        )}/decline`,
        { reason }
      )
    )
};

const signaturesBase = (clientId: string) =>
  `/foreign_nationals/${encodeURIComponent(clientId)}/signature-requests`;

const signatureAction =
  <B extends object>(action: string) =>
  (clientId: string, requestId: string, body?: B) =>
    call(() =>
      axiosInstance.post<FirmSignatureRequest>(
        `${signaturesBase(clientId)}/${encodeURIComponent(
          requestId
        )}/${action}`,
        body ?? {}
      )
    );

/** The firm sending documents for the client to sign in the portal. */
export const FirmSignaturesAPI = {
  list: (clientId: string) =>
    call(() =>
      axiosInstance.get<{ requests: FirmSignatureRequest[] }>(
        signaturesBase(clientId)
      )
    ).then((d) => d.requests),
  create: (clientId: string, draft: SignatureRequestDraft) =>
    call(() =>
      axiosInstance.post<FirmSignatureRequest>(signaturesBase(clientId), draft)
    ),
  /** Where a USCIS form asks for the person's own signature. */
  signaturePages: (clientId: string, documentId: string) =>
    call(() =>
      axiosInstance.get<{
        page_count: number;
        pages: { page: number; label: string }[];
      }>(
        `/foreign_nationals/${encodeURIComponent(
          clientId
        )}/documents/${encodeURIComponent(documentId)}/signature-pages`
      )
    ),
  open: signatureAction("open"),
  cancel: signatureAction("cancel"),
  review: signatureAction<{ accepted: boolean; note?: string }>("review")
};
